// ---------------------------------------------------------------------------
// Renderer: Legend
//
// Horizontal legend of colored dots/swatches + labels, plus optional group
// labels and flag glyphs.  Used to explain RAG status keys or evidence
// strength indicators beneath themed columns.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "../renderers/plainObjectRenderer";

export function renderLegend(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 300, imageCallback, theme, fontScale = 1 } = options;
  const s = fontScale;
  const themeColors = theme || defaultTheme;
  const group = new Konva.Group({ x, y, width });
  const items = block.items || [];
  if (items.length === 0) return group;

  const fontSize = Math.round(11 * s);
  const swatch = Math.round(9 * s);
  const dotGap = Math.round(6 * s);
  const itemGap = Math.round(16 * s);
  const groupGap = Math.round(26 * s);
  const verticalPad = Math.round(6 * s);

  const measure = (text, bold) => {
    const probe = new Konva.Text({ text: text || "", fontSize, fontFamily: "Arial", fontStyle: bold ? "bold" : "normal" });
    const w = probe.width();
    probe.destroy();
    return w;
  };

  const measured = items.map((item, i) => {
    if (item.group) {
      return { item, kind: "group", w: measure(item.group, true) + (i > 0 ? groupGap - itemGap : 0) };
    }
    const labelW = measure(item.label, false);
    return { item, kind: item.flag ? "flag" : "swatch", w: swatch + dotGap + labelW };
  });
  const totalW = measured.reduce((a, m) => a + m.w, 0) + (measured.length - 1) * itemGap;
  let cx = block.align === "left" ? 0
         : block.align === "right" ? Math.max(0, width - totalW)
         : Math.max(0, Math.round((width - totalW) / 2));
  const cy = verticalPad;

  for (const m of measured) {
    const { item, kind, w } = m;
    if (kind === "group") {
      group.add(new CustomText({
        x: cx + (w - measure(item.group, true)), y: cy,
        text: item.group, fontSize, fontStyle: "bold",
        letterSpacing: Math.round(0.5 * s),
        fill: themeColor(themeColors, "textSecondary", "#888"), refreshCallback: imageCallback
      }));
    } else if (kind === "flag") {
      const fy = cy + Math.round(1 * s);
      group.add(new Konva.Line({
        points: [cx, fy, cx, fy + swatch],
        stroke: item.color || "#B0892F", strokeWidth: Math.round(1.5 * s)
      }));
      group.add(new Konva.Line({
        points: [cx, fy, cx + swatch * 0.8, fy + swatch * 0.3, cx, fy + swatch * 0.55],
        closed: true, fill: item.color || "#B0892F"
      }));
      group.add(new CustomText({
        x: cx + swatch + dotGap, y: cy,
        text: item.label || "", fontSize,
        fill: themeColor(themeColors, "textSecondary", "#666"), refreshCallback: imageCallback
      }));
    } else {
      group.add(new Konva.Rect({
        x: cx, y: cy + Math.round(1 * s), width: swatch, height: swatch,
        fill: item.color || "#888", cornerRadius: Math.round(1.5 * s)
      }));
      group.add(new CustomText({
        x: cx + swatch + dotGap, y: cy,
        text: item.label || "", fontSize,
        fill: themeColor(themeColors, "textSecondary", "#666"), refreshCallback: imageCallback
      }));
    }
    cx += w + itemGap;
  }

  group.height(fontSize + verticalPad * 2);
  return group;
}
